import React, { useState } from 'react';
import { MapPin, ArrowRight } from 'lucide-react';
import { portfolioData } from '../mockData';

export const PortfolioSection = () => {
  const [filter, setFilter] = useState('All');
  const [hoveredId, setHoveredId] = useState(null);

  const categories = ['All', 'Residential', 'Hospitality', 'Commercial', 'Institutional'];

  const filteredProjects = filter === 'All' 
    ? portfolioData 
    : portfolioData.filter(project => project.category === filter);

  return (
    <section id="portfolio" className="py-20 bg-white relative overflow-hidden">
      {/* Decorative Background */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF6B00]/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#FF6B00]/5 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Title */}
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold text-black mb-4">
            Our <span className="text-[#FF6B00]">Projects</span>
          </h2>
          <div className="w-24 h-1.5 bg-gradient-to-r from-[#FF6B00] to-[#E65F00] mx-auto mb-6 rounded-full"></div>
          <p className="text-[#333333] text-xl max-w-3xl mx-auto leading-relaxed">
            Discover our exceptional turnkey projects that showcase precision, innovation, and excellence across India
          </p>
        </div>

        {/* Filter Tabs - Modern Design */}
        <div className="flex flex-wrap justify-center gap-3 mb-16">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-8 py-3 rounded-full font-semibold text-sm uppercase tracking-wide transition-all duration-300 ${
                filter === category
                  ? 'bg-gradient-to-r from-[#FF6B00] to-[#E65F00] text-white shadow-lg shadow-[#FF6B00]/30 scale-105'
                  : 'bg-gray-100 text-[#666666] hover:bg-gray-200 hover:text-black'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Portfolio Grid - Enhanced Bento Style */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              onMouseEnter={() => setHoveredId(project.id)}
              onMouseLeave={() => setHoveredId(null)}
              className={`group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 cursor-pointer ${
                index === 0 || index === 4 ? 'md:col-span-2 md:row-span-1 h-[400px]' : 'h-[350px]'
              } bg-white hover:scale-[1.02]`}
            >
              {/* Image */}
              <div className="absolute inset-0">
                <img
                  src={project.imageUrl}
                  alt={project.title}
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                />
                
                {/* Enhanced Gradient Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent group-hover:from-[#FF6B00]/90 group-hover:via-[#FF6B00]/60 group-hover:to-black/40 transition-all duration-500"></div>
                
                {/* Shine Effect on Hover */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                  <div className="absolute top-0 -left-full w-1/2 h-full bg-gradient-to-r from-transparent via-white/20 to-transparent group-hover:left-full transition-all duration-1000 ease-out"></div>
                </div>
              </div>

              {/* Content */}
              <div className="absolute inset-0 p-8 flex flex-col justify-end z-10">
                {/* Category Badge */}
                <div className="mb-4 transform translate-y-0 group-hover:-translate-y-2 transition-transform duration-300">
                  <span className="inline-block bg-white/20 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border border-white/30">
                    {project.category}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 transform translate-y-0 group-hover:-translate-y-2 transition-transform duration-300 leading-tight">
                  {project.title}
                </h3>

                {/* Location */}
                <div className="flex items-center text-white/90 mb-4 transform translate-y-0 group-hover:-translate-y-2 transition-transform duration-300 delay-75">
                  <MapPin size={18} className="mr-2 text-[#FF6B00] group-hover:text-white transition-colors" />
                  <span className="text-sm font-medium">{project.location}</span>
                </div>

                {/* Scope Badge - Appears on Hover */}
                {project.scope && (
                  <div className="mb-4 overflow-hidden transition-all duration-500 max-h-0 opacity-0 group-hover:max-h-20 group-hover:opacity-100">
                    <div className="inline-flex items-center bg-white/90 text-[#FF6B00] px-4 py-2 rounded-full text-xs font-bold shadow-lg">
                      <span className="w-2 h-2 bg-[#FF6B00] rounded-full mr-2 animate-pulse"></span>
                      {project.scope}
                    </div>
                  </div>
                )}

                {/* Description - Fades in on Hover */}
                <p className="text-white/95 text-sm leading-relaxed mb-4 overflow-hidden transition-all duration-500 max-h-0 opacity-0 group-hover:max-h-32 group-hover:opacity-100 delay-100 font-light">
                  {project.description}
                </p>

                {/* View Details Link */}
                <div className="flex items-center text-white font-semibold transition-all duration-500 opacity-0 group-hover:opacity-100 delay-150 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full w-fit hover:bg-white/20">
                  <span className="text-sm mr-2">Explore Project</span>
                  <ArrowRight size={18} className="transform transition-transform duration-300 group-hover:translate-x-1" />
                </div>

                {/* Decorative Element */}
                <div className="absolute bottom-0 left-0 w-0 h-1.5 bg-gradient-to-r from-[#FF6B00] to-white group-hover:w-full transition-all duration-700 ease-out shadow-lg shadow-[#FF6B00]/50"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-20 text-center">
          <div className="bg-gradient-to-r from-[#FF6B00] to-[#E65F00] rounded-3xl p-12 shadow-2xl">
            <h3 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Have a Project in Mind?
            </h3>
            <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
              Let's discuss how we can bring your vision to life with our turnkey interior solutions
            </p>
            <button
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-white text-[#FF6B00] px-10 py-4 rounded-full font-bold text-lg hover:bg-gray-100 transition-all duration-300 hover:scale-105 shadow-xl inline-flex items-center"
            >
              Get Started
              <ArrowRight className="ml-2" size={20} />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};
